import { Product } from "@/redux/services/menu/menu.type";
import { displayPrice, getImageUrl } from "@/lib/utils";
import { useAppSelector } from "@/redux/features/hooks";
import { useRestaurantCurrency } from "@/lib/hooks";
import { Pencil, Trash2 } from "lucide-react";
import { getProductDisplayPrice } from "@/lib/productPrice";

interface Props {
  product: Product;
  onEdit: (product: Product) => void;
  onDelete: (product: Product) => void;
  isReadOnly?: boolean;
  /**
   * Smallest option price in cents, shown as "À partir de" when the dish has
   * no own price (size-priced dishes, SFRESTO-146). See `src/lib/productPrice.ts`.
   */
  fromPrice?: number;
}

/**
 * Catalogue tile for a dish. Shares the menu item card's gallery look — round
 * image, centred name and price — so a dish looks the same whether you see it
 * in the catalogue or on a menu. The extras are catalogue-specific: where the
 * dish is used, and edit/delete.
 */
const ProductCard = ({
  product,
  onEdit,
  onDelete,
  isReadOnly,
  fromPrice,
}: Props) => {
  const currentRestaurant = useAppSelector((s) => s.user.currentRestaurant);
  const currency = useRestaurantCurrency();
  const menus = product.menuNames ?? [];
  const price = getProductDisplayPrice(product, fromPrice);

  return (
    <div
      className={`flex flex-col items-center bg-card border border-border p-4 rounded-lg transition-all ${
        isReadOnly ? "" : "cursor-pointer hover:shadow-md"
      }`}
      onClick={() => !isReadOnly && onEdit(product)}
    >
      <div className="w-24 h-24 sm:w-28 sm:h-28 overflow-hidden rounded-full border-2 border-border mb-3">
        <img
          src={getImageUrl(product.image?.url ?? currentRestaurant?.image?.url)}
          alt={product.title}
          className="w-full h-full object-cover"
        />
      </div>

      <h3 className="font-semibold text-center text-card-foreground text-sm mb-1">
        {product.title}
      </h3>

      <div
        className="text-primary font-bold text-base mb-3 text-center"
        data-testid="product-price"
      >
        {price.isFrom && (
          <span className="text-xs font-medium text-muted-foreground">
            À partir de{" "}
          </span>
        )}
        {displayPrice(price.amount, currency)}
      </div>

      {menus.length > 0 ? (
        <div className="flex flex-wrap justify-center gap-1.5">
          {menus.slice(0, 2).map((name) => (
            <span
              key={name}
              className="inline-flex items-center px-2 py-0.5 rounded-md bg-muted text-xs font-medium text-muted-foreground"
            >
              {name}
            </span>
          ))}
          {menus.length > 2 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-muted text-xs font-medium text-muted-foreground">
              +{menus.length - 2}
            </span>
          )}
        </div>
      ) : (
        <span className="text-xs text-muted-foreground italic">
          Sur aucun menu
        </span>
      )}

      {!isReadOnly && (
        <div
          className="mt-3 pt-3 w-full flex items-center justify-center gap-1 border-t border-border"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            aria-label="Modifier"
            onClick={() => onEdit(product)}
            className="p-1.5 rounded-md text-muted-foreground hover:text-primary hover:bg-muted transition-colors"
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label="Supprimer"
            onClick={() => onDelete(product)}
            className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-muted transition-colors"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export const ProductCardSkeleton = () => {
  return (
    <div className="flex flex-col items-center bg-card border border-border p-4 rounded-lg animate-pulse">
      <div className="w-24 h-24 sm:w-28 sm:h-28 bg-gray-200 rounded-full mb-3" />
      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
      <div className="h-5 bg-gray-200 rounded w-1/2 mb-3" />
      <div className="h-4 bg-gray-200 rounded w-2/3" />
    </div>
  );
};

export default ProductCard;
